'use client';

import React, { memo, useMemo } from 'react';
import AppIcon from './AppIcon';
import AppImage from './AppImage';
import { useTheme } from '@/contexts/ThemeContext';
import {
  LOGO_FOR_DARK_BACKGROUND,
  LOGO_FOR_LIGHT_BACKGROUND,
} from '@/lib/branding/logo-assets';

interface AppLogoProps {
  /** Svetla pozadina → logo-light; tamna pozadina → logo-dark; `theme` kao portal (tamna tema → logo-dark). */
  surface?: 'light' | 'dark' | 'theme';
  /** Kada je setovano, ignoriše `surface` i koristi eksplicitni fajl. */
  src?: string;
  iconName?: string; // Icon name when no image
  size?: number; // Size for icon/image
  className?: string; // Additional classes
  onClick?: () => void; // Click handler
}

const AppLogo = memo(function AppLogo({
  surface = 'theme',
  src: srcProp,
  iconName = 'SparklesIcon',
  size = 64,
  className = '',
  onClick,
}: AppLogoProps) {
  const { theme } = useTheme();

  const src = useMemo(() => {
    if (srcProp) return srcProp;
    if (surface === 'light') return LOGO_FOR_LIGHT_BACKGROUND;
    if (surface === 'dark') return LOGO_FOR_DARK_BACKGROUND;
    return theme === 'dark' ? LOGO_FOR_DARK_BACKGROUND : LOGO_FOR_LIGHT_BACKGROUND;
  }, [srcProp, surface, theme]);
  // Memoize className calculation
  const containerClassName = useMemo(() => {
    const classes = ['flex items-center'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  return (
    <div className={containerClassName} onClick={onClick}>
      {/* Show image if src provided, otherwise show icon */}
      {src ? (
        <AppImage
          src={src}
          alt="Top14"
          width={640}
          height={200}
          className="flex-shrink-0 w-auto max-w-[min(100vw-2rem,22rem)] h-auto object-contain object-left"
          style={{ height: size, width: 'auto' }}
          priority={true}
          suppressLoadingBackground
          unoptimized={src.endsWith('.svg')}
        />
      ) : (
        <AppIcon name={iconName} size={size} className="flex-shrink-0" />
      )}
    </div>
  );
});

export default AppLogo;
